import React, {useState} from "react";
import {Link} from "@inertiajs/react";
import {PiCaretDown, PiCaretRight} from "react-icons/pi";
import useAnalysisData from "./useAnalysisData.js";
import YearValueCells from "./YearValueCells.jsx";
import SignedPercentValue from "./SignedPercentValue.jsx";
import Sparkline from "./Sparkline.jsx";
import HeaderWithHelp from "./HeaderWithHelp.jsx";
import {formatEuroWhole} from "./format.js";

const NEXT_LEVEL = {continent: "country", country: "customer", article: "article_customer"};
const LEVEL_LABEL_PLURAL = {continent: "Kontinente", country: "Länder", customer: "Kunden", article: "Farbtöne oder Artikel", article_customer: "Kunden"};
const KUNDEN_EBENEN = ["customer", "article_customer"];

/**
 * Eine Ebene des Drilldowns (Produktlinie → Kontinent → Land → Kunde, oder
 * in der Aufschlüsselung nach Farbton Produktlinie → Farbton/Artikel →
 * Kunde; dort je Zeile zusätzlich der Vorjahresvergleich bis Stichmonat),
 * nachgeladen über GET /vertrieb/daten/drilldown, ohne Seitenwechsel. Wird
 * rekursiv für die jeweils nächste Ebene eingebunden und hängt ihre eigenen
 * Tabellenzeilen direkt unter die aufgeklappte Zeile - eine verschachtelte
 * <table> mit eigenem colspan wäre fehleranfälliger als gleich hohe,
 * eingerückte Zeilen in derselben Tabelle. `hideCurrent`/`hideHr` kommen von
 * der übergeordneten Tabelle (GesamtTable), damit die Spalten hier exakt zum
 * Tabellenkopf passen. `canLinkCustomer` blendet auf der Ebene "customer"
 * den Link auf die Kundenseite aus, wenn das Recht fehlt (reiner Text statt
 * Link, schnittstelle.md 7.1).
 */
export default function DrilldownLevel({baseParams, level, pastYears, indent, hideCurrent = false, hideHr = false, canLinkCustomer = true}) {
    const {data, loading, error} = useAnalysisData("/vertrieb/daten/drilldown", {...baseParams, level});
    const rows = data?.rows || [];
    const [expanded, setExpanded] = useState({});
    const nextLevel = NEXT_LEVEL[level];
    // Ohne die beiden Kopfspalten (Artikelart, Produktlinie), die die
    // Bezeichnung jeder Drilldown-Zeile gemeinsam belegt (colSpan 2) - vorher
    // stand die Bezeichnung nur in der ersten, und alle Beträge rutschten um
    // eine Spalte nach links unter die falsche Jahresüberschrift.
    const columnCount = pastYears.length + (hideCurrent ? 0 : 1) + (hideHr ? 0 : 1) + 2; // + Entwicklungslinie + Trend

    if (loading) {
        return (
            <tr>
                <td colSpan={columnCount + 2} style={{paddingLeft: indent}} className="text-muted small">
                    Wird geladen…
                </td>
            </tr>
        );
    }
    if (error) {
        return (
            <tr>
                <td colSpan={columnCount + 2} style={{paddingLeft: indent}} className="text-danger small">
                    {error}
                </td>
            </tr>
        );
    }
    if (!rows.length) {
        return (
            <tr>
                <td colSpan={columnCount + 2} style={{paddingLeft: indent}} className="text-muted small">
                    Keine {LEVEL_LABEL_PLURAL[level]} mit Umsatz im gewählten Zeitraum.
                </td>
            </tr>
        );
    }

    return rows.map((row) => (
        <React.Fragment key={row.key}>
            <tr>
                <td colSpan={2} style={{paddingLeft: indent}} className="text-nowrap">
                    {nextLevel && (
                        <button
                            type="button"
                            className="btn btn-sm btn-link p-0 me-1 align-baseline"
                            aria-expanded={Boolean(expanded[row.key])}
                            aria-label={expanded[row.key] ? "Zuklappen" : "Aufklappen"}
                            onClick={() => setExpanded((current) => ({...current, [row.key]: !current[row.key]}))}
                        >
                            {expanded[row.key] ? <PiCaretDown /> : <PiCaretRight />}
                        </button>
                    )}
                    {KUNDEN_EBENEN.includes(level) ? (
                        canLinkCustomer ? <Link href={`/vertrieb/kunde/${row.key}`}>{row.label}</Link> : row.label
                    ) : (
                        row.label
                    )}
                    {row.prev_period !== null && row.prev_period !== undefined && data?.prev_period_label && (
                        <div className="small text-muted" style={{paddingLeft: nextLevel ? 20 : 0}}>
                            <HeaderWithHelp help={`Umsatz im selben Zeitraum des Vorjahres (${data.prev_period_label}) und die Abweichung des laufenden Jahres bis zum Stichmonat dagegen.`}>
                                {data.prev_period_label}: {formatEuroWhole(row.prev_period)}
                            </HeaderWithHelp>
                            {" · "}
                            <SignedPercentValue value={row.vs_prev_period} />
                        </div>
                    )}
                </td>
                <YearValueCells years={row.years} current={row.current} hr={row.hr} pastYears={pastYears} hideCurrent={hideCurrent} hideHr={hideHr} />
                <td><Sparkline values={[...pastYears.map((year) => row.years?.[year]), row.hr]} /></td>
                <td className="text-end"><SignedPercentValue value={row.trend} /></td>
            </tr>
            {expanded[row.key] && nextLevel && (
                <DrilldownLevel
                    baseParams={{...baseParams, [level]: row.key}}
                    level={nextLevel}
                    pastYears={pastYears}
                    indent={indent + 20}
                    hideCurrent={hideCurrent}
                    hideHr={hideHr}
                    canLinkCustomer={canLinkCustomer}
                />
            )}
        </React.Fragment>
    ));
}
